import type { Logger } from 'pino';

import { logger as baseLogger } from '../log/logger';
import { resolveConfig } from '../config/Configurator';
import { openDatabase } from '../db/Database';
import { FilesRepo } from '../db/FilesRepo';
import { SnapshotsRepo } from '../db/SnapshotsRepo';
import { Outbox } from '../queue/Outbox';
import { createClients } from '../clients/Clients';
import { fetchVisitorRules } from '../rules/RulesFetcher';
import { compileRulesBundle } from '../rules/RulesCompiler';
import { Hasher } from '../hash/Hasher';
import type {
  CompiledRulesBundle,
  ResolvedConfig,
  RuntimeContext,
} from '../types';

export interface BootstrapResult extends RuntimeContext {
  filesRepo: FilesRepo;
  snapshotsRepo: SnapshotsRepo;
  hasher: Hasher;
}

function createServiceLogger(): Logger {
  return baseLogger.child({ scope: 'service' });
}

export async function bootstrap(params: {
  token?: string;
  rootPath?: string;
}): Promise<BootstrapResult> {
  const config: ResolvedConfig = await resolveConfig(params);
  const logger = createServiceLogger();

  const db = openDatabase(config.dbPath, logger.child({ scope: 'db' }));
  const filesRepo = new FilesRepo(db);
  const recovered = filesRepo.resetHashingStates();
  if (recovered > 0) {
    logger.info({ recovered }, 'Recovered lingering hashing leases');
  }
  const snapshotsRepo = new SnapshotsRepo(db);
  const outbox = new Outbox(db, logger);
  const clients = createClients(config, logger);

  const rules = await fetchVisitorRules(clients, logger);
  const compiled: CompiledRulesBundle = compileRulesBundle(rules);

  const hasher = new Hasher({ rootPath: config.rootPath, filesRepo, logger });

  const runtime: BootstrapResult = {
    config,
    logger,
    db,
    outbox,
    clients,
    rules: compiled,
    filesRepo,
    snapshotsRepo,
    hasher,
  };

  return runtime;
}
